import React, { useState } from "react";
import { Controller } from "react-hook-form";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import AsyncSelect from "../../../components/elements/inputs/AsyncSelect";
import useGet from "../../../hooks/useGet";
import {
  EMAIL_REGEX,
  PASSWORD_REGEX,
  PHONE_REGEX,
} from "../../../utilities/regex";
import { convertToSentenceCase } from "../../../utilities";
import { onNumberKeydown, onNumberFieldPaste } from "../../../utilities";

const CreateEditUserForm = ({
  isEdit = false,
  register,
  handleSubmit,
  errors,
  onSubmit,
  watch,
  userRolesOptions,
  control,
  getValues,
  setValue,
}) => {
  const [brandOptions, setBrandOptions] = useState([]);
  const [brandHasMore, setBrandHasMore] = useState(true);
  const [brandPageNo, setBrandPageNo] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { setParamsObject: setBrandParams, data: brandQuery } = useGet({
    url: "brand/list",
    initialParams: {
      pageNo: 0,
      limit: 10,
      searchQuery: "",
    },
    onSuccess: (response) => {
      const formattedOptions =
        response?.data?.map((brand) => ({
          value: brand.brand_id,
          label: brand.name,
        })) || [];

      if (brandPageNo === 0) {
        setBrandOptions(formattedOptions);
      } else {
        setBrandOptions((prev) => [...prev, ...formattedOptions]);
      }

      setBrandHasMore(
        formattedOptions.length > 0 &&
        response?.totalCount > brandOptions.length
      );
    },
  });

  const fetchBrands = (query = "", reset = false) => {
    const pageToFetch = reset ? 0 : brandPageNo;

    if (reset) {
      setBrandPageNo(0);
      setBrandOptions([]);
    }

    setBrandParams({
      pageNo: pageToFetch,
      limit: 10,
      searchQuery: query,
    });

    if (!reset) {
      setBrandPageNo((prev) => prev + 1);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
      <div className="mt-5 gap-5 grid grid-cols-1 md:grid-cols-2">
        <div>
          <label className="label">
            First Name <small className="text-red-700">*</small>
          </label>
          <input
            type="text"
            placeholder="First Name"
            className="input"
            {...register("first_name", {
              required: "First name is required",
            })}
          />
          {errors.first_name && (
            <span className="text-red-500 text-sm">
              {errors.first_name.message}
            </span>
          )}
        </div>

        <div>
          <label className="label">
            Last Name <small className="text-red-700">*</small>
          </label>
          <input
            type="text"
            placeholder="Last Name"
            className="input"
            {...register("last_name", { required: "Last name is required" })}
          />
          {errors.last_name && (
            <span className="text-red-500 text-sm">
              {errors.last_name.message}
            </span>
          )}
        </div>

        <div>
          <label className="label">
            Email <small className="text-red-700">*</small>
          </label>
          {!isEdit && (
            <input
              type="text"
              placeholder="Email"
              className="input"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: EMAIL_REGEX,
                  message: "Please enter a valid email address",
                },
              })}
            />
          )}
          {isEdit && (
            <div className="input bg-white opacity-60">
              <p>{watch("email")}</p>
            </div>
          )}
          {errors.email && (
            <span className="text-red-500 text-sm">{errors.email.message}</span>
          )}
        </div>

        <div>
          <label className="label">
            Phone <small className="text-red-700">*</small>
          </label>
          <input
            type="number"
            placeholder="Phone"
            className="input"
            {...register("phone", {
              required: "Phone is required",
              pattern: {
                value: PHONE_REGEX,
                message: "Please enter a valid phone number",
              },
            })}
            onKeyDown={(e) =>
              onNumberKeydown(e, { isPositiveNumber: true, isInteger: true })
            }
            onPaste={(e) =>
              onNumberFieldPaste(e, { isPositiveNumber: true, isInteger: true })
            }
            onWheel={(e) => e.target.blur()}
          />
          {errors.phone && (
            <span className="text-red-500 text-sm">{errors.phone.message}</span>
          )}
        </div>

        {!isEdit && (
          <div>
            <label className="label">
              Password <small className="text-red-700">*</small>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                className="input"
                {...register("password", {
                  required: "Password is required",
                  pattern: {
                    value: PASSWORD_REGEX,
                    message:
                      "Password must contain at least 6 characters, 1 uppercase letter, 1 lowercase letter, 1 number and 1 special character",
                  },
                })}
              />
              <button
                type="button"
                className="absolute right-3 top-3 text-gray-500"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <AiOutlineEyeInvisible size={20} />
                ) : (
                  <AiOutlineEye size={20} />
                )}
              </button>
            </div>
            {errors.password && (
              <span className="text-red-500 text-sm">
                {errors.password.message}
              </span>
            )}
          </div>
        )}

        {!isEdit && (
          <div>
            <label className="label">
              Confirm password <small className="text-red-700">*</small>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                className="input"
                {...register("confirm_password", {
                  required: "Confirm password is required",
                  validate: (value) =>
                    value === watch("password") || "Passwords do not match",
                })}
              />
              <button
                type="button"
                className="absolute right-3 top-3 text-gray-500"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <AiOutlineEyeInvisible size={20} />
                ) : (
                  <AiOutlineEye size={20} />
                )}
              </button>
            </div>
            {errors.confirm_password && (
              <span className="text-red-500 text-sm">
                {errors.confirm_password.message}
              </span>
            )}
          </div>
        )}

        <div>
          <label className="label">
            Role <small className="text-red-700">*</small>
          </label>
          {!isEdit && (
            <select
              className="input"
              {...register("user_role", { required: "Role is required" })}
            >
              <option value="">Select Role</option>
              {userRolesOptions.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          )}
          {isEdit && (
            <div className="input bg-white opacity-60">
              <p>{convertToSentenceCase(watch("user_role"))}</p>
            </div>
          )}
          {errors.user_role && (
            <span className="text-red-500 text-sm">
              {errors.user_role.message}
            </span>
          )}
        </div>

        {watch("user_role") === "brand_handler" && (
          <div>
            <label className="label">
              Brand <small className="text-red-700">*</small>
            </label>
            <Controller
              name="brand_id"
              control={control}
              rules={{ required: "Brand is required" }}
              render={({ field: { onChange, value } }) => (
                <AsyncSelect
                  // label="Select Brand"
                  options={brandOptions}
                  hasMore={brandHasMore}
                  fetchNext={fetchBrands}
                  isLoading={brandQuery?.isFetching}
                  value={value}
                  onChange={(val) => {
                    onChange(val);
                    const selectedBrand = brandOptions.find(
                      (b) => b.value === val
                    );
                    if (selectedBrand) {
                      setValue("brand_name", selectedBrand.label);
                    }
                  }}
                  defaultLabel={getValues("brand_name")}
                />
              )}
            />
            {errors.brand_id && (
              <span className="text-red-500 text-sm">
                {errors.brand_id.message}
              </span>
            )}
          </div>
        )}
      </div>

      <input
        type="submit"
        className="px-4 py-1 bg-red-600 text-white rounded-md mt-5 mx-auto"
        value={isEdit ? "Update User" : "Add User"}
      />
    </form>
  );
};

export default CreateEditUserForm;
